import { env, Native } from "@hb/mbridge";
import { Toast } from "vant";
import { RouterTicketPayResult } from "../../constants/routerConstants";
import { uiMgr } from "../../common/uiMgr";
import nativeMgr from "../../common/native/native";
import { appQueryConfig } from "../../common/utils/url";
import { httpMgr } from "../../common/network/httpMgr";

import type { PayInfo, SkuData, TicketTabData } from "../store/modules/ticket";
import {
  BuyMultiGoodsItem,
  CombiProduct,
  reqLimitOrderRes,
  reqBuyMultiGoods,
  requestBuyGoods,
  requestBuyGoodslimit,
  wxAppIdOrderPreCheck,
} from "./ticketApis";
import { pay, reqOrderPay } from "./pay";
import { OrderListItem } from "../store/modules/order";
import { getCouponMallOrderDetailUrl } from "../utils";
import { ApiEnums, CouponPromoTypeEnum } from "../../constants/apiConstants";
import logger from "../../common/utils/logger";
import { handlePostOrderRefresh } from "./reward";
import store from "../store";

export type SkuParamData = Pick<
  SkuData,
  | "activityInfo"
  | "skuId"
  | "spuId"
  | "salePrice"
  | "categoryId"
  | "isSuperUser"
  | "promotionInfos"
> &
  Partial<Pick<SkuData, "algorithmPriceFlag">> & {
    num?: number;
  };

let isBuyingGoods = false;
let curBuySku: SkuParamData | null = null;
let curPayInfo: PayInfo | null | undefined = null;
const orderPort = 1;

/** 购买类型 */
export enum BuyType {
  /** 普通商品 */
  NORMAL = 1,
  /** 限时购商品 */
  LIMIT,
}

export const reqBuyGoods = async ({
  skuData,
  buyType,
  isPushPage,
  payAmount,
  svipAmount,
  combiProduct,
  highRecm,
  hideOrder,
  chargeType,
  multiNumOrder = false,
  isFromDetail = false,
}: {
  skuData: SkuParamData;
  payAmount: number;
  openId?: string;
  buyType: BuyType;
  isPushPage?: boolean;
  svipAmount?: number;
  combiProduct?: CombiProduct;
  highRecm?: string;
  chargeType?: number;
  multiNumOrder?: boolean;
  hideOrder?: boolean;
  /** 是否来自详情页/浮层入口（用于刷新数据时区分入口） */
  isFromDetail?: boolean;
}): Promise<void> => {
  if (isBuyingGoods) {
    return;
  }

  curBuySku = skuData;
  curPayInfo = null;

  if (!curBuySku) {
    return;
  }

  let deviceHash = "";

  try {
    ({
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      "fingerprint-hash": deviceHash,
    } = await Native.getDeviceFingerprintHash());
  } catch (error) {
    // noop
  }

  const reqParams = {
    hideOrder,
    chargeType,
    purchaseNum: skuData.num || 1,
    multiNumOrder,
    orderPort,
    deviceHash,
    appId: appQueryConfig.appId,
    version: (await nativeMgr.getDeviceInfo()).version,
    marketActivityCode: appQueryConfig.market_activity_code || undefined,
    isSuperUser: !!svipAmount,
    pointDiscountAmount: svipAmount,
    // 添加注释： 1.路由 中拿到algorithmPriceFlag 传给商品详情接口  2.商详接口也返回了  3.前端存到store的curSku里
    // algorithmPriceFlag: skuData.algorithmPriceFlag,

    // 传入走算法标识: 1.路由获取  2.商详接口返回
    algorithmPriceFlag:
      appQueryConfig.algorithmPriceFlag || skuData.algorithmPriceFlag,
    // 页面来源：（1..路由获取, 2..卡券商城平台公域：couponMall
    // adsource: appQueryConfig.adsource || router.app.$route.query.adsource,
    // applicationScene: appQueryConfig.applicationScene,
    applicationScene: "couponMall", // 卡券商城固定applicationScene
  };

  try {
    isBuyingGoods = true;
    logger("当前地址栏 URL:", window.location.href);
    logger("URL appQueryConfig 参数:", appQueryConfig);
    if (env.isWechatMini) {
      // appQueryConfig：  url上的参数配置
      const { appId, adsource } = appQueryConfig;
      const params = {
        spuIds: [skuData.spuId],
        appId: appId || null,
        adsource,
      };
      const {
        data: { checkResult },
      } = await wxAppIdOrderPreCheck(params);
      if (!checkResult) {
        Toast("checkResult返回了false appId预警");
        return;
      }
    }
    if (buyType === BuyType.LIMIT) {
      const {
        activityInfo, skuId, spuId, categoryId,
      } = skuData;
      const { activityCode, salePrice } = activityInfo!;

      // * 抢购商品下单
      const activityToken = await requestBuyGoodslimit({
        ...reqParams,
        activityCode,
        skuId,
        spuId,
        salePrice,
        payAmount: salePrice,
      });

      if (activityToken) {
        // * 限时购商品需要轮询下单结果
        uiMgr.showLoading();

        try {
          /** 轮询限购下单结果 */
          const loopReqLimitOrderRes = async () => {
            try {
              const payOrderInfo = await reqLimitOrderRes({ activityToken });

              if (payOrderInfo) {
                uiMgr.hideLoading();
                // * 调起收银台支付
                const payInfo = {
                  payOrderInfo,
                  skuData,
                  categoryId,
                  combiProduct,
                  svipAmount,
                  highRecm,
                };
                pay(payInfo, { isPushPage }).catch(() => {
                  curPayInfo = payInfo;
                });
              } else {
                window.setTimeout(loopReqLimitOrderRes, 5000);
              }
            } catch (e) {
              // noop
            }
          };
          window.setTimeout(loopReqLimitOrderRes, 1000);
        } catch (e) {
          // noop
        }
      }

      return;
    }

    const getOrderItem = ({
      skuId,
      spuId,
      salePrice,
      num = 1,
      promotionInfos,
      itemExtra = { isTyingFree: false },
    }: SkuParamData & {
      itemExtra?: {
        isTyingFree: boolean;
      };
    }) => {
      // promotionInfos 存在则直接透传
      if (promotionInfos?.length) {
        return {
          skuId,
          spuId,
          salePrice,
          num,
          promotionInfos,
          itemExtra,
        };
      }

      return {
        skuId,
        spuId,
        salePrice,
        num,
        itemExtra,
      };
    };

    const itemList: BuyMultiGoodsItem[] = [
      {
        ...getOrderItem({ ...curBuySku, itemExtra: { isTyingFree: false } }),
        pointDiscountAmount: svipAmount,
      },
    ];

    if (combiProduct) {
      itemList.push(
        getOrderItem({
          ...combiProduct,
          num: 1,
          itemExtra: { isTyingFree: true },
        }),
      );
      reqParams.purchaseNum += 1;
    }

    const orderData = await reqBuyMultiGoods({
      ...reqParams,
      payAmount,
      itemList,
    });

    if (!orderData) {
      return;
    }

    // 下单后刷新数据（需求变更V2）
    try {
      const allSpuIds = Object.values(store.state.ticket.skuDatas)
        .flatMap((tabData: TicketTabData) => (
          tabData.list?.map((item) => String(item.spuId)) || []
        ));
      // 追加超值精选商品 spuId 并去重
      const greatValueSpuIds = (store.state.ticket.greatValueModule || [])
        .map((item) => String(item.spuId))
        .filter((id) => id && id !== 'undefined');
      const dedupedSpuIds = [...new Set([...allSpuIds, ...greatValueSpuIds])];
      const curSkuDetail = store.state.ticket.curSku;
      logger('刷新数据 allSpuIds以及当前spuId', dedupedSpuIds, curBuySku?.spuId);
      handlePostOrderRefresh({
        promotionInfos: curBuySku?.promotionInfos,
        optionalPromotions: curSkuDetail?.optionalPromotions,
        isFromDetail,
        categoryId: curBuySku?.categoryId || '',
        algorithmPriceFlag: appQueryConfig.algorithmPriceFlag || curBuySku?.algorithmPriceFlag,
        allSpuIds: dedupedSpuIds,
      });
    } catch {
      // 刷新失败不阻塞购买流程
    }

    const multiOrders = Array.isArray(orderData);
    logger("orderData", orderData);

    // 奖励金0元下单直接成功不需要拉起收银台
    // 判断是否使用了奖励金：旧奖励金(svipAmount) 或 新奖励金(promotionInfos 中有 couponPromoType=4)
    const hasNewRewardPromo = !!(curBuySku?.promotionInfos?.some(
      (p) => String(p.couponPromoType) === CouponPromoTypeEnum.REWARD_POINTS,
    ));
    const hasRewardDiscount = svipAmount || hasNewRewardPromo;
    if (
      hasRewardDiscount
      && !multiOrders
      && orderData.payMoney
      && Number(orderData.payMoney) === 0
    ) {
      logger("奖励金0元单独下单", orderData.payMoney);
      uiMgr.pushPage({
        name: RouterTicketPayResult.name,
        query: {
          isSuccess: "1",
          billNo: orderData.orderId,
          categoryId: `${curBuySku.categoryId}`,
        },
      });
      return;
    }

    const categoryId = curBuySku?.categoryId || "";
    const payInfo = {
      payOrderInfo: {
        ...orderData,
        billNo: orderData.orderId,
      },
      categoryId,
      skuData: curBuySku,
      combiProduct,
      svipAmount,
      highRecm,
    };
    pay(payInfo, { isPushPage }).catch(() => { curPayInfo = payInfo; });
  } finally {
    isBuyingGoods = false;
  }
};

/**
 * actionRePay 重新支付
 */
export const actionRePay = async ({
  info,
  isPushPage,
  successCallback,
}: {
  info?: PayInfo;
  isPushPage?: boolean;
  successCallback?: () => void;
} = {}) => {
  logger("进入重新支付流程：actionRePay info", info);
  if (info) {
    curPayInfo = info;
  }

  if (!curPayInfo) {
    return;
  }

  const { payOrderInfo } = curPayInfo;
  logger("重新支付流程：payOrderInfo", payOrderInfo);
  const resolvedOrders = Array.isArray(payOrderInfo)
    ? payOrderInfo
    : [payOrderInfo];
  logger("重新支付流程：resolvedOrders", resolvedOrders);
  if (resolvedOrders.length > 1) {
    logger("多商品重新支付流程：resolvedOrders.length > 1，跳转订单列表");
    uiMgr.redirectPage("/order");
    return;
  }

  const [order] = resolvedOrders;

  try {
    const req: {
      orderId: string;
      orderSource?: number;
      activityCode?: string;
      priceType?: number;
    } = {
      orderId: order.billNo,
    };

    if (info?.orderSource) {
      req.orderSource = info.orderSource;
    }
    if (info?.activityCode) {
      req.activityCode = info.activityCode;
      req.priceType = 2;
    }

    const payOrderInfoNew = await requestBuyGoods(req);
    pay(
      {
        ...curPayInfo,
        payOrderInfo: payOrderInfoNew,
      },
      { isPushPage, successCallback },
    );
  } finally {
    // isRePaying = false
  }
};

/** 多商品订单重新下单支付 */
export const reqMultiGoodsRepay = async (order: OrderListItem) => {
  const { orderId, payPrice, goodsList } = order;
  logger("多商品订单重新下单支付：reqBuyMultiGoods方法调用");
  const data = await reqBuyMultiGoods({
    orderId,
    payAmount: payPrice,
    itemList: goodsList.map(
      ({
        skuId, spuId, salePrice, num, promotionInfos,
      }) => ({
        skuId,
        spuId,
        salePrice,
        num: num || 1,
        promotionInfos,
      }),
    ),
  });

  if (!data) {
    return;
  }

  logger("多商品订单重新下单支付：reqOrderPay方法调用");
  await reqOrderPay({
    ...data,
    returnUrl: getCouponMallOrderDetailUrl(orderId),
  });
  Toast({
    message: "支付成功",
    onClose() {
      logger(
        "调试adsource：多商品订单重新下单支付：支付成功，刷新页面",
        window.location.href,
      );
      window.location.reload();
    },
  });
};

/** 秒杀下单 */

export const reqSeckill = async (
  data: Record<string, unknown>,
): Promise<{
  activityToken: string;
}> => {
  const res = await httpMgr.request(ApiEnums.SECKILL_KILL, {
    ...data,
    method: "queryMapPoiBranch",
  });
  return res.data;
};

/** 查询订单结果 */
export const queryOrderResult = async (
  data: Record<string, unknown>,
): Promise<{
  activityToken: string;
}> => {
  const res = await httpMgr.request(ApiEnums.SECKILL_KILL_ORDER, {
    ...data,
  });
  return res.data;
};
